import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare, RefreshCw, Settings, CheckCircle, XCircle, Send, Users,
  Upload, Phone, FileText, BarChart3, Clock, Search, Filter, Plus,
  Eye, Trash2, Play, Building2, Shield, AlertTriangle
} from 'lucide-react'
import { whatsappApi, WhatsAppContact, WhatsAppTemplate, WhatsAppCampaign } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

type TabType = 'dashboard' | 'contacts' | 'templates' | 'campaigns' | 'config'

// Customer type badge colors
const customerTypeBadge: Record<string, { bg: string; text: string }> = {
  'PVD': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300' },
  'VIP': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300' },
  'PVP': { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-800 dark:text-slate-300' },
}

export default function WhatsAppPage() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState<TabType>('dashboard')
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [contactSearch, setContactSearch] = useState('')
  const [contactFilter, setContactFilter] = useState<string>('')

  // Queries
  const statusQuery = useQuery({
    queryKey: ['whatsapp-status'],
    queryFn: whatsappApi.getStatus,
    refetchInterval: 60000
  })

  const configQuery = useQuery({
    queryKey: ['whatsapp-config'],
    queryFn: whatsappApi.getConfig
  })

  const contactsQuery = useQuery({
    queryKey: ['whatsapp-contacts', contactSearch, contactFilter],
    queryFn: () => whatsappApi.getContacts({
      search: contactSearch || undefined,
      customer_type: contactFilter || undefined,
      limit: 100
    }),
    enabled: statusQuery.data?.configured === true
  })

  const templatesQuery = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => whatsappApi.getTemplates(),
    enabled: statusQuery.data?.configured === true
  })

  const campaignsQuery = useQuery({
    queryKey: ['whatsapp-campaigns'],
    queryFn: () => whatsappApi.getCampaigns(undefined, 50),
    enabled: statusQuery.data?.configured === true
  })

  // Mutations
  const importPrestaShopMutation = useMutation({
    mutationFn: (params: { customerType?: string; withConsent?: boolean }) =>
      whatsappApi.importFromPrestaShop(params.customerType, params.withConsent),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts'] })
      queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] })
      toast.success(`Importados ${data.imported} contactos desde PrestaShop`)
    },
    onError: (error: any) => {
      toast.error('Error importando contactos', error.response?.data?.detail || error.message)
    }
  })

  const updateSettingsMutation = useMutation({
    mutationFn: whatsappApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] })
      toast.success('Configuracion actualizada')
    },
    onError: (error: any) => {
      toast.error('Error actualizando configuracion', error.response?.data?.detail || error.message)
    }
  })

  const sendTestMutation = useMutation({
    mutationFn: (phone: string) => whatsappApi.sendTestMessage(phone, 'Mensaje de prueba desde JVS Web'),
    onSuccess: () => {
      toast.success('Mensaje de prueba enviado')
    },
    onError: (error: any) => {
      toast.error('Error enviando mensaje', error.response?.data?.detail || error.message)
    }
  })

  const isConnected = statusQuery.data?.connected === true
  const isConfigured = configQuery.data?.is_configured === true

  const tabs: { id: TabType; name: string; icon: any }[] = [
    { id: 'dashboard', name: 'Dashboard', icon: BarChart3 },
    { id: 'contacts', name: 'Contactos', icon: Users },
    { id: 'templates', name: 'Plantillas', icon: FileText },
    { id: 'campaigns', name: 'Campanas', icon: Send },
    { id: 'config', name: 'Configuracion', icon: Settings },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            WhatsApp Marketing
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Gestiona comunicaciones y campanas por WhatsApp con Twilio
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => statusQuery.refetch()}
            className="btn btn-secondary"
            disabled={statusQuery.isRefetching}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", statusQuery.isRefetching && "animate-spin")} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className={cn(
        "card p-4 flex items-center justify-between",
        isConnected
          ? "border-green-200 dark:border-green-800 bg-success-50 dark:bg-success-900/20"
          : isConfigured
          ? "border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20"
          : "border-red-200 dark:border-red-800 bg-danger-50 dark:bg-danger-900/20"
      )}>
        <div className="flex items-center gap-3">
          {isConnected ? (
            <CheckCircle className="w-6 h-6 text-success-600 dark:text-success-400" />
          ) : isConfigured ? (
            <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
          ) : (
            <XCircle className="w-6 h-6 text-danger-600 dark:text-danger-400" />
          )}
          <div>
            <p className={cn(
              "font-medium",
              isConnected ? "text-green-800 dark:text-green-200" :
              isConfigured ? "text-yellow-800 dark:text-yellow-200" :
              "text-red-800 dark:text-red-200"
            )}>
              {isConnected ? 'Conectado a Twilio WhatsApp' :
               isConfigured ? 'Configurado pero sin conexion' :
               'No configurado'}
            </p>
            {isConnected && configQuery.data?.whatsapp_from && (
              <p className="text-sm text-success-600 dark:text-success-400">
                Numero: {configQuery.data.whatsapp_from}
              </p>
            )}
            {!isConfigured && (
              <p className="text-sm text-danger-600 dark:text-danger-400">
                Configura las credenciales de Twilio para comenzar
              </p>
            )}
          </div>
        </div>
        {statusQuery.isLoading && (
          <RefreshCw className="w-5 h-5 animate-spin text-slate-500" />
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm",
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-500 hover:text-gray-700 hover:border-slate-300 dark:text-slate-400 dark:hover:text-slate-300"
              )}
            >
              <tab.icon className={cn(
                "mr-2 h-5 w-5",
                activeTab === tab.id
                  ? "text-primary-500"
                  : "text-slate-400 group-hover:text-slate-500 dark:text-slate-500"
              )} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Total Contactos</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {configQuery.data?.total_contacts || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-success-100 dark:bg-green-900/30">
                    <Shield className="w-5 h-5 text-success-600 dark:text-success-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Con Consentimiento</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {configQuery.data?.consented_contacts || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Plantillas</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {templatesQuery.data?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                    <Send className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Campanas</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {campaignsQuery.data?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Notificaciones Automaticas
                </h2>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Notificaciones de Factura</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Enviar WhatsApp cuando se publica una factura
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={configQuery.data?.invoice_notifications_enabled || false}
                        onChange={(e) => updateSettingsMutation.mutate({ invoice_notifications: e.target.checked })}
                        className="sr-only peer"
                        disabled={!isConfigured}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Seguimiento de Pedidos</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Enviar actualizaciones de estado de pedido
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={configQuery.data?.order_tracking_enabled || false}
                        onChange={(e) => updateSettingsMutation.mutate({ order_tracking: e.target.checked })}
                        className="sr-only peer"
                        disabled={!isConfigured}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Campaigns */}
            {campaignsQuery.data && campaignsQuery.data.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Campanas Recientes
                  </h2>
                </div>
                <div className="card-body p-0">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Campana
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Enviados
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Entregados
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Leidos
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {campaignsQuery.data.slice(0, 5).map((campaign) => (
                        <tr key={campaign.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900 dark:text-white">{campaign.name}</p>
                            {campaign.target_customer_type && (
                              <span className={cn(
                                "text-xs px-2 py-0.5 rounded",
                                customerTypeBadge[campaign.target_customer_type]?.bg || 'bg-slate-100',
                                customerTypeBadge[campaign.target_customer_type]?.text || 'text-slate-600'
                              )}>
                                {campaign.target_customer_type}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                            {campaign.sent_count}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                            {campaign.delivered_count}
                          </td>
                          <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                            {campaign.read_count}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              campaign.status === 'completed'
                                ? "bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : campaign.status === 'sending'
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                : campaign.status === 'draft'
                                ? "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            )}>
                              {campaign.status === 'completed' ? 'Completada' :
                               campaign.status === 'sending' ? 'Enviando' :
                               campaign.status === 'draft' ? 'Borrador' :
                               campaign.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar por telefono, nombre o email..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    className="input pl-10 w-full"
                  />
                </div>
                <select
                  value={contactFilter}
                  onChange={(e) => setContactFilter(e.target.value)}
                  className="input"
                >
                  <option value="">Todos los tipos</option>
                  <option value="PVD">PVD - Distribuidores</option>
                  <option value="VIP">VIP</option>
                  <option value="PVP">PVP - Particulares</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => importPrestaShopMutation.mutate({ withConsent: false })}
                  disabled={importPrestaShopMutation.isPending || !isConfigured}
                  className="btn btn-secondary"
                >
                  <Upload className={cn("w-4 h-4 mr-2", importPrestaShopMutation.isPending && "animate-spin")} />
                  Importar PrestaShop
                </button>
              </div>
            </div>

            {/* Contacts Table */}
            <div className="card">
              <div className="card-body p-0">
                {contactsQuery.isLoading ? (
                  <div className="text-center py-8 text-slate-500">Cargando contactos...</div>
                ) : contactsQuery.data?.contacts && contactsQuery.data.contacts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Contacto
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Telefono
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Tipo
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Consentimiento
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            WhatsApp Valido
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Ultimo Mensaje
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {contactsQuery.data.contacts.map((contact) => (
                          <tr key={contact.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">
                                  {contact.customer_name || 'Sin nombre'}
                                </p>
                                {contact.customer_email && (
                                  <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {contact.customer_email}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-900 dark:text-white">{contact.phone}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {contact.customer_type && (
                                <span className={cn(
                                  "px-2 py-1 rounded text-xs font-medium",
                                  customerTypeBadge[contact.customer_type]?.bg || 'bg-slate-100',
                                  customerTypeBadge[contact.customer_type]?.text || 'text-slate-600'
                                )}>
                                  {contact.customer_type}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {contact.consent ? (
                                <CheckCircle className="w-5 h-5 text-success-500 mx-auto" />
                              ) : (
                                <XCircle className="w-5 h-5 text-danger-500 mx-auto" />
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {contact.valid_whatsapp === 1 ? (
                                <CheckCircle className="w-5 h-5 text-success-500 mx-auto" />
                              ) : contact.valid_whatsapp === 0 ? (
                                <XCircle className="w-5 h-5 text-danger-500 mx-auto" />
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                              {contact.last_message_at
                                ? new Date(contact.last_message_at).toLocaleDateString('es-ES')
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-500 dark:text-slate-400 mb-4">No hay contactos</p>
                    <button
                      onClick={() => importPrestaShopMutation.mutate({ withConsent: false })}
                      disabled={importPrestaShopMutation.isPending || !isConfigured}
                      className="btn btn-primary"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Importar desde PrestaShop
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templatesQuery.isLoading ? (
                <div className="col-span-full text-center py-8 text-slate-500">Cargando plantillas...</div>
              ) : templatesQuery.data && templatesQuery.data.length > 0 ? (
                templatesQuery.data.map((template) => (
                  <div key={template.id} className="card">
                    <div className="card-body">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white">{template.name}</h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{template.description}</p>
                        </div>
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-medium",
                          template.template_type === 'transactional'
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            : "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                        )}>
                          {template.template_type === 'transactional' ? 'Transaccional' : 'Marketing'}
                        </span>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap line-clamp-4">
                          {template.body_template}
                        </p>
                      </div>
                      {template.variables && template.variables.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {template.variables.map((v: string) => (
                            <span key={v} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-600 dark:text-slate-300">
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-slate-500 dark:text-slate-400">No hay plantillas configuradas</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button className="btn btn-primary" disabled={!isConfigured}>
                <Plus className="w-4 h-4 mr-2" />
                Nueva Campana
              </button>
            </div>

            <div className="card">
              <div className="card-body p-0">
                {campaignsQuery.isLoading ? (
                  <div className="text-center py-8 text-slate-500">Cargando campanas...</div>
                ) : campaignsQuery.data && campaignsQuery.data.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Campana
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Destinatarios
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Enviados
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Entregados
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Leidos
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Fallidos
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Estado
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                        {campaignsQuery.data.map((campaign) => (
                          <tr key={campaign.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{campaign.name}</p>
                                {campaign.description && (
                                  <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">
                                    {campaign.description}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                              {campaign.total_recipients}
                            </td>
                            <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                              {campaign.sent_count}
                            </td>
                            <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                              {campaign.delivered_count}
                            </td>
                            <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                              {campaign.read_count}
                            </td>
                            <td className="px-4 py-3 text-center text-danger-600 dark:text-danger-400">
                              {campaign.failed_count}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={cn(
                                "px-2 py-1 rounded-full text-xs font-medium",
                                campaign.status === 'completed'
                                  ? "bg-success-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                  : campaign.status === 'sending'
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                  : campaign.status === 'draft'
                                  ? "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-400"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                              )}>
                                {campaign.status === 'completed' ? 'Completada' :
                                 campaign.status === 'sending' ? 'Enviando' :
                                 campaign.status === 'draft' ? 'Borrador' :
                                 campaign.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button className="p-1 text-slate-500 hover:text-gray-700 dark:hover:text-slate-300">
                                  <Eye className="w-4 h-4" />
                                </button>
                                {campaign.status === 'draft' && (
                                  <button className="p-1 text-primary-500 hover:text-primary-700">
                                    <Play className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Send className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                    <p className="text-slate-500 dark:text-slate-400 mb-4">No hay campanas creadas</p>
                    <button className="btn btn-primary" disabled={!isConfigured}>
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Primera Campana
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            {/* Twilio Credentials */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Credenciales Twilio
                </h2>
              </div>
              <div className="card-body">
                <TwilioCredentialsForm
                  isConfigured={isConfigured}
                  currentFrom={configQuery.data?.whatsapp_from}
                  onSuccess={() => {
                    queryClient.invalidateQueries({ queryKey: ['whatsapp-status'] })
                    queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] })
                  }}
                />
              </div>
            </div>

            {/* Test Message */}
            {isConfigured && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Mensaje de Prueba
                  </h2>
                </div>
                <div className="card-body">
                  <TestMessageForm onSend={(phone) => sendTestMutation.mutate(phone)} isPending={sendTestMutation.isPending} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Not Configured State */}
      {!isConfigured && activeTab !== 'config' && (
        <div className="card p-8 text-center">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Configura Twilio para empezar
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Conecta tu cuenta de Twilio WhatsApp Business para enviar mensajes y campanas
          </p>
          <button
            onClick={() => setActiveTab('config')}
            className="btn btn-primary"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurar Twilio
          </button>
        </div>
      )}
    </div>
  )
}

// Twilio Credentials Form Component
function TwilioCredentialsForm({ isConfigured, currentFrom, onSuccess }: {
  isConfigured: boolean
  currentFrom?: string | null
  onSuccess: () => void
}) {
  const toast = useToast()
  const [accountSid, setAccountSid] = useState('')
  const [authToken, setAuthToken] = useState('')
  const [whatsappFrom, setWhatsappFrom] = useState('')

  const setCredentialsMutation = useMutation({
    mutationFn: whatsappApi.setCredentials,
    onSuccess: () => {
      toast.success('Credenciales guardadas correctamente')
      onSuccess()
      setAccountSid('')
      setAuthToken('')
      setWhatsappFrom('')
    },
    onError: (error: any) => {
      toast.error('Error guardando credenciales', error.response?.data?.detail || error.message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setCredentialsMutation.mutate({
      account_sid: accountSid,
      auth_token: authToken,
      whatsapp_from: whatsappFrom
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isConfigured && (
        <div className="p-4 bg-success-50 dark:bg-success-900/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Credenciales configuradas</span>
          </div>
          {currentFrom && (
            <p className="text-sm text-success-600 dark:text-success-400 mt-1">
              Numero: {currentFrom}
            </p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Account SID
        </label>
        <input
          type="text"
          value={accountSid}
          onChange={(e) => setAccountSid(e.target.value)}
          placeholder={isConfigured ? '••••••••••••••••' : 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
          className="input w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Auth Token
        </label>
        <input
          type="password"
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          placeholder={isConfigured ? '••••••••••••••••' : 'Tu auth token de Twilio'}
          className="input w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Numero WhatsApp (From)
        </label>
        <input
          type="text"
          value={whatsappFrom}
          onChange={(e) => setWhatsappFrom(e.target.value)}
          placeholder="whatsapp:+14155238886"
          className="input w-full"
        />
        <p className="text-xs text-slate-500 mt-1">
          Formato: whatsapp:+[codigo pais][numero]. Ej: whatsapp:+14155238886 (Sandbox)
        </p>
      </div>

      <button
        type="submit"
        disabled={setCredentialsMutation.isPending || !accountSid || !authToken || !whatsappFrom}
        className="btn btn-primary"
      >
        {setCredentialsMutation.isPending ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Settings className="w-4 h-4 mr-2" />
        )}
        {isConfigured ? 'Actualizar Credenciales' : 'Guardar Credenciales'}
      </button>
    </form>
  )
}

// Test Message Form Component
function TestMessageForm({ onSend, isPending }: { onSend: (phone: string) => void; isPending: boolean }) {
  const [phone, setPhone] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (phone) {
      onSend(phone)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Numero de destino
        </label>
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="whatsapp:+34612345678"
          className="input w-full"
        />
        <p className="text-xs text-slate-500 mt-1">
          El numero debe estar registrado en el Sandbox de Twilio para pruebas
        </p>
      </div>

      <button
        type="submit"
        disabled={isPending || !phone}
        className="btn btn-secondary"
      >
        {isPending ? (
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Send className="w-4 h-4 mr-2" />
        )}
        Enviar Mensaje de Prueba
      </button>
    </form>
  )
}
